function finiteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function updateSelectedSegmentMeasurement(
  state,
  field,
  value,
  { min = 0, max = Number.POSITIVE_INFINITY, step = 1, decimals = 2 } = {}
) {
  const selected = state.selectedObjects?.roofSegments || [];
  const segments = (state.segments || []).map((segment, index) => {
    if (!selected.includes(index)) return segment;
    const current = finiteNumber(segment?.[field]);
    const requested =
      value === '+' ? current + step : value === '-' ? current - step : value;
    const next = Math.min(max, Math.max(min, finiteNumber(requested)));
    return {
      ...segment,
      [field]: next.toFixed(decimals)
    };
  });
  return { ...state, segments };
}

export function deleteSelectedRoofSegments(state) {
  const selected = new Set(state.selectedObjects?.roofSegments || []);
  return {
    ...state,
    segments: (state.segments || []).filter((_, index) => !selected.has(index))
  };
}

export function moveSelectedRoofVertices(state, offsetX, offsetY) {
  const selectedObjects = state.selectedObjects || {};
  const roofVertices = selectedObjects.roofVertices || {};
  const fireSetbacks = selectedObjects.fireSetbacks || {};
  const segments = (state.segments || []).map((segment, index) => {
    const selected = fireSetbacks[index] || roofVertices[index];
    if (!Array.isArray(selected) || !selected.length) return segment;
    const selectedVertices = new Set(selected);
    return {
      ...segment,
      geometry: (segment.geometry || []).map((vertex, vertexIndex) =>
        selectedVertices.has(vertexIndex)
          ? [
              finiteNumber(vertex?.[0]) + finiteNumber(offsetX),
              finiteNumber(vertex?.[1]) + finiteNumber(offsetY)
            ]
          : Array.isArray(vertex)
            ? [...vertex]
            : vertex
      )
    };
  });
  return { ...state, segments };
}

const _shadingNumbers = (value) => {
  if (value == null || value === '') return [];
  const source = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [value];
  return source
    .flat()
    .map((item) => Number(String(item).trim()))
    .filter((item) => Number.isFinite(item));
};

const _isUniformOnes = (value) => {
  const numbers = _shadingNumbers(value);
  return (
    numbers.length > 0 && numbers.every((item) => Math.abs(item - 1) < 0.001)
  );
};

const _hasCalculatedShading = (value) =>
  _shadingNumbers(value).some((item) => Math.abs(item - 1) >= 0.001);

export function retainCalculatedShading(existing, incoming) {
  if (incoming == null || incoming === '') return existing;
  if (_hasCalculatedShading(existing) && _isUniformOnes(incoming)) {
    return existing;
  }
  return incoming;
}

export function applySegmentShadingFactors(
  state,
  [tsrf = [], tof = [], solarAccess = []] = []
) {
  return {
    ...state,
    segments: (state.segments || []).map((segment, index) => ({
      ...segment,
      solarAccess: retainCalculatedShading(
        segment.solarAccess ?? segment.solar_access,
        solarAccess[index]
      ),
      tsrf: retainCalculatedShading(segment.tsrf, tsrf[index]),
      tof: retainCalculatedShading(segment.tof, tof[index])
    })),
    shadingHasRun: true,
    shadingNeedsRerun: false,
    shadingRerunNoticeShown: false
  };
}

export function applyServerSideSegmentShadingFactors(state, factors) {
  const segments = state.segments || [];
  if (
    !Array.isArray(factors) ||
    !segments.length ||
    factors.length < segments.length
  ) {
    return state;
  }
  return {
    ...state,
    segments: segments.map((segment, index) => {
      const source = factors[index];
      if (!source) return segment;
      return {
        ...segment,
        ...(source.solarAccess == null
          ? {}
          : {
              solarAccess: retainCalculatedShading(
                segment.solarAccess ?? segment.solar_access,
                source.solarAccess
              )
            }),
        ...(source.tsrf == null
          ? {}
          : { tsrf: retainCalculatedShading(segment.tsrf, source.tsrf) }),
        ...(source.tof == null
          ? {}
          : { tof: retainCalculatedShading(segment.tof, source.tof) })
      };
    }),
    shadingHasRun: true,
    shadingNeedsRerun: false,
    shadingRerunNoticeShown: false
  };
}

export function resolveDesignerDsmParams(current, next) {
  const currentBias = Number(current?.bias);
  const nextBias = Number(next?.bias);
  const keepCurrentBias =
    Number.isFinite(currentBias) &&
    currentBias >= 0 &&
    current?.biasSource !== 'fallback' &&
    next?.biasSource !== 'measured' &&
    Number.isFinite(nextBias) &&
    nextBias >= 0 &&
    currentBias !== nextBias;
  return keepCurrentBias ? { ...next, bias: current.bias } : next;
}

export function appendDesignerAnnotation(
  annotations,
  position,
  type,
  params = {}
) {
  const current = Array.isArray(annotations) ? annotations : [];
  const typeName = String(type || '').toLowerCase();
  const typeCaption = typeName === 'custom' ? 'Annotation' : type;
  const typeLength = current.filter(
    (annotation) => String(annotation?.type || '').toLowerCase() === typeName
  ).length;
  return [
    ...current,
    {
      position: { ...position },
      text: `${typeCaption}_${typeLength + 1}`,
      type,
      ...params
    }
  ];
}

function translateGeometry(items, offsetX, offsetY, scaleX, scaleY, center) {
  return (items || []).map((item) => ({
    ...item,
    geometry: (item.geometry || []).map((vertex) => [
      (finiteNumber(vertex?.[0]) - center) * scaleX +
        finiteNumber(offsetX) +
        center,
      (finiteNumber(vertex?.[1]) - center) * scaleY +
        finiteNumber(offsetY) +
        center
    ])
  }));
}

export function translateDesignerGeometry(
  state,
  {
    offsetX,
    offsetY,
    flipX,
    flipY,
    translateRoofs,
    translateObstacles,
    translateTrees,
    canvasSize
  }
) {
  const scaleX = flipX ? -1 : 1;
  const scaleY = flipY ? -1 : 1;
  const center = finiteNumber(canvasSize, 1024) / 2;
  return {
    segments: translateRoofs
      ? translateGeometry(
          state.segments,
          offsetX,
          offsetY,
          scaleX,
          scaleY,
          center
        )
      : state.segments,
    obstacles: translateObstacles
      ? translateGeometry(
          state.obstacles,
          offsetX,
          offsetY,
          scaleX,
          scaleY,
          center
        )
      : state.obstacles,
    trees: translateTrees
      ? translateGeometry(state.trees, offsetX, offsetY, scaleX, scaleY, center)
      : state.trees
  };
}
